import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './styles/styles.module.css';
import { trimText } from './utils/trimText';
import { deactivateShiftToDo } from '../../api/logEntries/post';
import { setShiftToDoItems } from '../../Redux/slices/shiftToDoSlice';
import { useDispatch } from 'react-redux';

type Priority = 'high' | 'med' | 'low';

interface Item {
  _id: string; // <-- ObjectId from backend
  text: string;
  level?: Priority;
}

interface ListWidgetProps {
  width: number;
  height: number;
  headline: string;
  itemsToRender: Item[]; // <-- ensure your parent passes _id
}

type WidgetItemProps = {
  item: Item;
  onOpen: (item: Item) => void;
};

const WidgetItem: React.FC<WidgetItemProps> = ({ item, onOpen }) => {
  const { text, level } = item;
  return (
    <div
      className={`${styles.widgetItemWrapper} ${level === 'high' ? styles.high : ''}`}
      tabIndex={0}
      role="button"
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item);
        }
      }}
      aria-label={`Open details for: ${trimText(text, 60)}`}
    >
      <p className={styles.text}>{trimText(text, 60)}</p>
    </div>
  );
};

/** Centered modal with both buttons */
const CenterModal: React.FC<{
  isOpen: boolean;
  title?: string;
  onClose: () => void;
  onAcknowledgeDelete: () => void;
  loading?: boolean;
  children: React.ReactNode;
}> = ({
  isOpen,
  title = 'Details',
  onClose,
  onAcknowledgeDelete,
  loading = false,
  children,
}) => {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => closeBtnRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className={styles.modalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="widget-modal-title"
      >
        <div className={styles.modalHeader}>
          <h3 id="widget-modal-title" className={styles.modalTitle}>
            {title}
          </h3>
          <button
            ref={closeBtnRef}
            className={styles.closeBtn}
            aria-label="Close dialog"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>{children}</div>

        <div className={styles.modalFooter}>
          <button
            className={styles.modalSecondaryButton}
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
          <button
            className={styles.modalPrimaryButton}
            onClick={onAcknowledgeDelete}
            disabled={loading}
          >
            {loading ? 'Working…' : 'Acknowledge & Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ListWidget: React.FC<ListWidgetProps> = ({
  width,
  height,
  headline,
  itemsToRender,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Item | null>(null);
  const [list, setList] = useState<Item[]>(itemsToRender);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  // keep local list in sync if parent updates
  useEffect(() => setList(itemsToRender), [itemsToRender]);

  const handleOpen = (item: Item) => {
    setSelected(item);
    setOpen(true);
  };

  const handleClose = () => {
    if (loading) return; // avoid closing mid-action
    setOpen(false);
    setSelected(null);
  };

  const handleAcknowledgeDelete = async () => {
    if (!selected?._id) return;
    try {
      setLoading(true);
      // call your backend to mark inactive by ObjectId
      const res = await deactivateShiftToDo(selected._id);
      console.log(res);
      dispatch(setShiftToDoItems((res as any).data));

      setOpen(false);
      setSelected(null);
    } catch (err) {
      console.error('Failed to deactivate item', err);
      // You could show a toast/snackbar here
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.wrapper} style={{ width: `${width}vw` }}>
        <div className={styles.headlineContainer}>
          <p className={styles.headlineText}>{headline}</p>
        </div>
        <div
          style={{ height: `${height}vh` }}
          className={styles.itemsContainer}
        >
          {list.map((item) => (
            <WidgetItem key={item._id} item={item} onOpen={handleOpen} />
          ))}
        </div>
      </div>

      <CenterModal
        isOpen={open}
        onClose={handleClose}
        onAcknowledgeDelete={handleAcknowledgeDelete}
        loading={loading}
        title={selected?.level === 'high' ? 'High Priority' : 'Message'}
      >
        <p className={styles.fullText}>{selected?.text}</p>
      </CenterModal>
    </>
  );
};

export default ListWidget;
